import React, { useState, useEffect, useRef } from 'react';
import { 
  Building2, 
  Home, 
  Eye, 
  Camera, 
  Maximize2, 
  RotateCcw, 
  ZoomIn, 
  ZoomOut,
  MapPin,
  Ruler,
  Palette,
  Settings,
  Download,
  Upload,
  Share2,
  Play,
  Pause,
  SkipBack,
  X,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  SkipForward,
  Layers,
  Grid3X3,
  Move3D,
  Lightbulb,
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Info,
  Star,
  Heart,
  Bookmark
} from 'lucide-react';
import FloorPlanUpload from '../components/FloorPlanUpload';
import FloorPlanSVGGenerator from '../components/FloorPlanSVGGenerator';
import PhotoGallery from '../components/PhotoGallery';
import ThreeDViewer from '../components/ThreeDViewer';

const PropertyVisualizerPage = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [currentFloor, setCurrentFloor] = useState('4og');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [viewMode, setViewMode] = useState('floorplan'); // floorplan, photos, 3d, ar
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showVirtualStaging, setShowVirtualStaging] = useState(false);
  const [stagingStyle, setStagingStyle] = useState('modern');
  const [showFinancialOverlay, setShowFinancialOverlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showFloorPlanUpload, setShowFloorPlanUpload] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedRoomPhotos, setSelectedRoomPhotos] = useState([]);
  const [currentPhotoInModal, setCurrentPhotoInModal] = useState(0);
  
  // Clara OCR Integration
  const [claraOCRResult, setClaraOCRResult] = useState(null);
  const [showClaraFloorPlan, setShowClaraFloorPlan] = useState(false);
  const [selectedClaraRoom, setSelectedClaraRoom] = useState(null);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Mannheim Maisonette Property Data
  const mannheimProperty = {
    id: 'mannheim-d31-maisonette',
    name: 'Maisonette-Wohnung Mannheim D3,1',
    address: 'Mannheim D3,1, Deutschland',
    size: '103 qm',
    price: '€ 450.000',
    pricePerSqm: '€ 4.369/qm',
    rooms: 4,
    bedrooms: 3,
    bathrooms: 1,
    floors: ['4og', 'dg'],
    type: 'maisonette',
    status: 'for_sale',
    features: ['Wendeltreppe', 'Dachfenster', '2 Balkone', 'Offene Küche'],
    
    floorPlans: {
      '4og': {
        name: '4. Obergeschoss',
        image: '/assets/property-visualizer/mannheim-d31/floor-4og.jpg',
        rooms: {
          wohnzimmer: {
            name: 'Wohnzimmer',
            size: '80.4 x 67.2 cm',
            area: '54 qm',
            type: 'living',
            coordinates: { x: 200, y: 150, width: 300, height: 200 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006814.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006812.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006810.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006807.jpg'
            ]
          },
          kueche: {
            name: 'Küche',
            size: '25 qm',
            area: '25 qm',
            type: 'kitchen',
            coordinates: { x: 520, y: 150, width: 150, height: 120 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006815.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006816.jpg'
            ]
          },
          gaeste_wc: {
            name: 'Gäste-WC',
            size: '4 qm',
            area: '4 qm',
            type: 'bathroom',
            coordinates: { x: 680, y: 280, width: 80, height: 60 },
            photos: []
          },
          balkon: {
            name: 'Balkon',
            size: '8 qm',
            area: '8 qm',
            type: 'balcony',
            coordinates: { x: 120, y: 80, width: 100, height: 60 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006809.jpg'
            ]
          },
          wendeltreppe: {
            name: 'Wendeltreppe',
            size: '6 qm',
            area: '6 qm',
            type: 'stairs',
            coordinates: { x: 350, y: 280, width: 80, height: 80 },
            photos: []
          }
        }
      },
      'dg': {
        name: 'Dachgeschoss',
        image: '/assets/property-visualizer/mannheim-d31/floor-dg.jpg',
        rooms: {
          schlafzimmer_1: {
            name: 'Schlafzimmer I',
            size: '20 qm',
            area: '20 qm',
            type: 'bedroom',
            coordinates: { x: 150, y: 120, width: 200, height: 150 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006840.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006842.jpg'
            ]
          },
          schlafzimmer_2: {
            name: 'Schlafzimmer II',
            size: '18 qm',
            area: '18 qm',
            type: 'bedroom',
            coordinates: { x: 380, y: 120, width: 180, height: 140 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006847.jpg'
            ]
          },
          schlafzimmer_3: {
            name: 'Schlafzimmer III',
            size: '15 qm',
            area: '15 qm',
            type: 'bedroom',
            coordinates: { x: 580, y: 120, width: 160, height: 130 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006834.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006844.jpg'
            ]
          },
          badezimmer: {
            name: 'Badezimmer',
            size: '8 qm',
            area: '8 qm',
            type: 'bathroom',
            coordinates: { x: 200, y: 300, width: 120, height: 100 },
            photos: [
              '/assets/property-visualizer/mannheim-d31/general/1000006805.jpg',
              '/assets/property-visualizer/mannheim-d31/general/1000006806.jpg'
            ]
          }
        }
      }
    }
  };

  useEffect(() => {
    setSelectedProperty(mannheimProperty);
  }, []);

  // Clara OCR Integration - Floor plan processing completion handler
  const handleFloorPlanProcessingComplete = (processingResults) => {
    console.log('Clara OCR processing completed:', processingResults);
    
    if (processingResults && processingResults.success) {
      // Store Clara OCR results
      setClaraOCRResult(processingResults);
      
      // Switch to Clara OCR view
      setShowClaraFloorPlan(true);
      
      // Close upload modal
      setShowFloorPlanUpload(false);
      
      console.log('Clara OCR Floor Plan activated with', processingResults.rooms_detected, 'rooms');
    } else {
      console.error('Clara OCR processing failed:', processingResults);
    }
  };

  // Handle Clara OCR room clicks
  const handleClaraRoomClick = (room, roomIndex) => {
    console.log('Clara room clicked:', room, roomIndex);
    setSelectedClaraRoom(roomIndex);
    
    // For now, show a simple modal with room info
    // Later this could integrate with photo galleries
    setSelectedRoom({
      name: room.room_name || `Raum ${roomIndex + 1}`,
      area: room.area_text || `${Math.round(room.area_pixels / 1000)} qm (geschätzt)`,
      type: 'clara_detected',
      photos: [] // Could be populated from existing photo library
    });
    setShowRoomModal(true);
  };

  // Toggle between original and Clara OCR floor plans
  const toggleFloorPlanView = () => {
    if (claraOCRResult) {
      setShowClaraFloorPlan(!showClaraFloorPlan);
      setSelectedClaraRoom(null);
      setSelectedRoom(null);
    }
  };

  const handleRoomClick = (roomKey) => {
    if (showClaraFloorPlan) return; // Clara OCR rooms handled separately
    
    const currentFloorData = selectedProperty.floorPlans[currentFloor];
    const room = currentFloorData.rooms[roomKey];
    
    if (room) {
      setSelectedRoom(room);
      setShowRoomModal(true);
    }
  };

  const handlePhotoClick = (photos, startIndex = 0) => {
    setSelectedRoomPhotos(photos);
    setCurrentPhotoInModal(startIndex);
    setShowPhotoModal(true);
  };

  const nextPhoto = () => {
    setCurrentPhotoInModal((prev) => 
      prev < selectedRoomPhotos.length - 1 ? prev + 1 : 0
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoInModal((prev) => 
      prev > 0 ? prev - 1 : selectedRoomPhotos.length - 1
    );
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!selectedProperty) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Property Visualizer wird geladen...</p>
        </div>
      </div>
    );
  }

  const currentFloorData = selectedProperty.floorPlans[currentFloor];

  const renderContent = () => {
    switch (viewMode) {
      case 'floorplan':
        return (
          <div 
            ref={containerRef}
            className="relative w-full h-full overflow-hidden bg-gray-50 dark:bg-black"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {showClaraFloorPlan && claraOCRResult ? (
              <FloorPlanSVGGenerator
                claraOCRResult={claraOCRResult}
                onRoomClick={handleClaraRoomClick}
                selectedRoom={selectedClaraRoom}
                className="w-full h-full"
                style={{
                  transform: `scale(${zoomLevel}) translate(${panPosition.x}px, ${panPosition.y}px)`,
                  transformOrigin: 'center center',
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
              />
            ) : (
              <div
                className="relative w-full h-full flex items-center justify-center"
                style={{
                  transform: `scale(${zoomLevel}) translate(${panPosition.x}px, ${panPosition.y}px)`,
                  transformOrigin: 'center center',
                  cursor: isDragging ? 'grabbing' : 'grab'
                }}
              >
                <img
                  src={currentFloorData.image}
                  alt={currentFloorData.name}
                  className="max-w-full max-h-full object-contain"
                  draggable={false}
                />
                {Object.entries(currentFloorData.rooms).map(([roomKey, room]) => (
                  <div
                    key={roomKey}
                    className="absolute cursor-pointer group"
                    style={{
                      left: `${room.coordinates.x}px`,
                      top: `${room.coordinates.y}px`,
                      width: `${room.coordinates.width}px`,
                      height: `${room.coordinates.height}px`,
                    }}
                    onClick={() => handleRoomClick(roomKey)}
                  >
                    <div className="absolute inset-0 bg-blue-500 bg-opacity-20 border-2 border-blue-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-2 left-2 bg-white dark:bg-slate-900 bg-opacity-90 px-2 py-1 rounded text-sm font-medium text-gray-900 dark:text-white shadow-sm">
                      {room.name}
                    </div>
                    <div className="absolute bottom-2 left-2 bg-white dark:bg-slate-900 bg-opacity-90 px-2 py-1 rounded text-xs text-gray-600 dark:text-gray-300 shadow-sm">
                      {room.area}
                    </div>
                    {room.photos && room.photos.length > 0 && (
                      <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium shadow-sm">
                        {room.photos.length}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'photos':
        return <PhotoGallery isActive={true} selectedProperty={selectedProperty} />;
      case '3d':
        return <ThreeDViewer isActive={true} selectedProperty={selectedProperty} />;
      case 'ar':
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
            <div className="text-center">
              <Eye className="w-16 h-16 mx-auto mb-4" />
              <h2 className="text-2xl font-bold">AR-Modus</h2>
              <p className="mt-2 text-gray-400">In Kürze verfügbar. Scannen Sie einen QR-Code, um das Modell in Ihrem Raum zu platzieren.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header - Following Bewirtschaftung Design Pattern */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Building2 className="w-8 h-8 mr-3 text-blue-600" />
                Property Visualizer
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Immobilien-Visualisierung und interaktive Grundrisse
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              
              <button 
                onClick={() => setShowFloorPlanUpload(true)}
                className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                title="Neuen Grundriss hochladen und digitalisieren"
              >
                <Upload className="w-4 h-4 mr-2" />
                Grundriss Upload
              </button>
              
              {/* Clara OCR Toggle Button */}
              {claraOCRResult && (
                <button 
                  onClick={toggleFloorPlanView}
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showClaraFloorPlan 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  title={showClaraFloorPlan ? 'Zurück zum Original Floor Plan' : 'Clara OCR Floor Plan anzeigen'}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  'Clara OCR'
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Property Info Card - Following Bewirtschaftung Card Style */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedProperty.name}
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Zum Verkauf
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Adresse</label>
              <p className="text-gray-900 dark:text-white">{selectedProperty.address}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Größe</label>
              <p className="text-gray-900 dark:text-white">{selectedProperty.size}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Zimmer</label>
              <p className="text-gray-900 dark:text-white">{selectedProperty.rooms}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Preis</label>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{selectedProperty.price}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{selectedProperty.pricePerSqm}</p>
            </div>
          </div>
          
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Besonderheiten</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedProperty.features.map((feature, index) => (
                <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* View Mode Tabs - Following Bewirtschaftung Button Style */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ansicht wechseln</h3>
            
            {viewMode === 'floorplan' && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Zoom: {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.1))}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.1))}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setViewMode('floorplan')}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'floorplan'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Home className="w-4 h-4 mr-2" />
              Grundriss
            </button>
            <button
              onClick={() => setViewMode('photos')}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'photos'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Camera className="w-4 h-4 mr-2" />
              Fotos
            </button>
            <button
              onClick={() => setViewMode('3d')}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === '3d'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Move3D className="w-4 h-4 mr-2" />
              3D
            </button>
            <button
              onClick={() => setViewMode('ar')}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'ar'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Eye className="w-4 h-4 mr-2" />
              AR
            </button>
          </div>
        </div>

        {/* Floor Selector - Following Bewirtschaftung Card Style */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Etage wechseln</h3>
          <div className="flex flex-wrap gap-3">
            {selectedProperty.floors.map((floor) => (
              <button
                key={floor}
                onClick={() => setCurrentFloor(floor)}
                className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentFloor === floor
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {selectedProperty.floorPlans[floor].name}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area - Following Bewirtschaftung Card Style */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {showClaraFloorPlan && claraOCRResult 
                  ? `Clara OCR Analyse (${claraOCRResult.rooms_detected} Räume erkannt)`
                  : currentFloorData.name
                }
              </h3>
              
              {showClaraFloorPlan && claraOCRResult && (
                <div className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Live Clara OCR</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="h-[600px] w-full">
            {renderContent()}
          </div>
        </div>

        {/* Controls - Following Bewirtschaftung Card Style */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Erweiterte Optionen</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Virtual Staging</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Virtuelle Möblierung anzeigen</p>
              </div>
              <button
                onClick={() => setShowVirtualStaging(!showVirtualStaging)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showVirtualStaging ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-800'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showVirtualStaging ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Preis-Overlay</label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Preisangaben im Grundriss</p>
              </div>
              <button
                onClick={() => setShowFinancialOverlay(!showFinancialOverlay)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showFinancialOverlay ? 'bg-blue-600' : 'bg-gray-200 dark:bg-slate-800'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showFinancialOverlay ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Room Modal */}
      {showRoomModal && selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedRoom.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedRoom.area}</p>
              </div>
              <button
                onClick={() => {
                  setShowRoomModal(false);
                  setSelectedRoom(null);
                  setSelectedClaraRoom(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            
            <div className="p-6">
              {selectedRoom.photos && selectedRoom.photos.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">Fotos ({selectedRoom.photos.length})</h4>
                    <button
                      onClick={() => handlePhotoClick(selectedRoom.photos, 0)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                    >
                      Alle Fotos anzeigen
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {selectedRoom.photos.slice(0, 6).map((photo, index) => (
                      <div
                        key={index}
                        className="aspect-square bg-gray-100 dark:bg-slate-800 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handlePhotoClick(selectedRoom.photos, index)}
                      >
                        <img
                          src={photo}
                          alt={`${selectedRoom.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Camera className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Keine Fotos für diesen Raum verfügbar</p>
                  {selectedRoom.type === 'clara_detected' && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                      Raum wurde durch Clara OCR erkannt
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {showPhotoModal && selectedRoomPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              onClick={() => setShowPhotoModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="w-8 h-8" />
            </button>
            
            <button
              onClick={prevPhoto}
              className="absolute left-4 text-white hover:text-gray-300 z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            
            <button
              onClick={nextPhoto}
              className="absolute right-4 text-white hover:text-gray-300 z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
            
            <img
              src={selectedRoomPhotos[currentPhotoInModal]}
              alt={`Photo ${currentPhotoInModal + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
              {currentPhotoInModal + 1} / {selectedRoomPhotos.length}
            </div>
            
            {/* Thumbnail Navigation */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 mt-8">
              {selectedRoomPhotos.map((photo, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPhotoInModal(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    index === currentPhotoInModal ? 'border-white' : 'border-transparent opacity-60 hover:opacity-80'
                  }`}
                >
                  <img
                    src={photo}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Floor Plan Upload Modal */}
      <FloorPlanUpload
        isOpen={showFloorPlanUpload}
        onClose={() => setShowFloorPlanUpload(false)}
        onProcessingComplete={handleFloorPlanProcessingComplete}
      />
    </div>
  );
};

export default PropertyVisualizerPage;
